import type { ExternalICalBlocking_ResourceReservation } from "@@/shared/acquire/resources/resource-reservation";
import { resourceReservationIdZodSchema } from "@@/shared/acquire/resources/resource-reservation-id";
import type { ProviderId } from "@@/shared/providers/provider-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { z } from "zod";
import {
    agendaItem_ExternalICalOrderDataFactory,
    agendaItem_ExternalICalOrderDataZodObject,
} from "./agenda-item-external-ical-order-data";
import type { AgendaResourceData } from "./agenda-resource-data";

const agendaItem_ExternalICalOrderZodObject = z.object({
    _id: resourceReservationIdZodSchema,
    _type: z.literal("AGENDA_ITEM"),
    type: z.literal("EXTERNAL_ICAL_ORDER"),
    providerId: providerIdZodSchema,
    description: z.string().optional(),
    hasComments: z.boolean().default(false),
    hasWarnings: z.boolean().default(false),
    externalSource: z.string().optional(),
    externalId: z.string().optional(),

    // Reservations
    reservation: agendaItem_ExternalICalOrderDataZodObject,
});
type AgendaItem_ExternalICalOrder = z.infer<
    typeof agendaItem_ExternalICalOrderZodObject
>;

const agendaItem_ExternalICalOrderFactory = {
    from: (params: {
        providerId: ProviderId;
        reservation: ExternalICalBlocking_ResourceReservation;
        resource: AgendaResourceData;
    }): AgendaItem_ExternalICalOrder => {
        const { providerId, reservation, resource } = params;
        return {
            _id: reservation._id,
            _type: "AGENDA_ITEM",
            type: "EXTERNAL_ICAL_ORDER",
            providerId,
            hasComments: false,
            hasWarnings: false,
            description: reservation.description,
            externalId: reservation.externalId,
            externalSource: reservation.source,
            reservation: agendaItem_ExternalICalOrderDataFactory.from({
                reservation,
                resource,
            }),
        };
    },
};

const isAgendaItem_ExternalICalOrder = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is AgendaItem_ExternalICalOrder => {
    return item._type === "AGENDA_ITEM" && item.type === "EXTERNAL_ICAL_ORDER";
};

export {
    agendaItem_ExternalICalOrderFactory,
    agendaItem_ExternalICalOrderZodObject,
    isAgendaItem_ExternalICalOrder,
};
export type { AgendaItem_ExternalICalOrder };
